"use client";

import { cn } from "@/lib/utils";
import { AnimatePresence, motion } from "framer-motion";
import React from "react";
import useMeasure from "react-use-measure";
import { Drawer } from "vaul";
import { CloseIcon } from "./icons";
import "./styles.css";

export default function FamilyDrawer({
  isOpen,
  setIsOpen,
  content,
  contentKey,
  className,
  title,
}: {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  content: React.ReactNode;
  contentKey: string;
  className?: string;
  title?: string;
}) {
  return (
    <Drawer.Root open={isOpen} onOpenChange={setIsOpen}>
      <Drawer.Portal>
        <Drawer.Overlay className="fixed inset-0 z-10 bg-black/30" onClick={() => setIsOpen(false)} />

        <Drawer.Content
          className={cn(
            "fixed inset-x-4 bottom-4 z-10",
            "flex flex-col",
            "justify-end",
            "bg-transparent outline-none",
            "mx-auto max-w-[381px]  md:mx-auto md:w-full",
            "h-fit min-h-[30vh]",
            className,
          )}
        >
          {title && <Drawer.Title className="w-full px-6 text-left font-sans font-medium">{title}</Drawer.Title>}
          {content}
        </Drawer.Content>
      </Drawer.Portal>
    </Drawer.Root>
  );
}

export function DynamicDrawer({
  isOpen,
  setIsOpen,
  content,
  contentKey,
  className,
}: {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  content: React.ReactNode;
  contentKey: string;
  className?: string;
}) {
  const [elementRef, bounds] = useMeasure();
  const previousHeightRef = React.useRef<number>(undefined);

  const opacityDuration = React.useMemo(() => {
    const currentHeight = bounds.height;
    const previousHeight = previousHeightRef.current;

    const MIN_DURATION = 0.15;
    const MAX_DURATION = 0.27;

    if (!previousHeightRef.current) {
      previousHeightRef.current = currentHeight;
      return MIN_DURATION;
    }

    const heightDifference = Math.abs(currentHeight - (previousHeight ?? 0));
    previousHeightRef.current = currentHeight;

    const duration = Math.min(Math.max(heightDifference / 500, MIN_DURATION), MAX_DURATION);

    return duration;
  }, [bounds.height]);

  return (
    <Drawer.Root open={isOpen} onOpenChange={setIsOpen}>
      <Drawer.Portal>
        <Drawer.Overlay className="fixed inset-0 z-10 bg-black/10 backdrop-blur-sm" onClick={() => setIsOpen(false)} />
        <Drawer.Content
          asChild
          className={cn(
            "light overflow-hidden rounded-[36px] bg-[#FEFFFE] font-open-runde outline-none",
            "max-w-[361px] md:w-full",
            "mx-auto md:mx-auto",
            "fixed inset-x-4 bottom-4 z-10",
            className,
          )}
        >
          <motion.div
            animate={{
              height: bounds.height,
              transition: {
                duration: 0.27,
                ease: [0.25, 1, 0.5, 1],
              },
            }}
          >
            <Drawer.Close asChild>
              <button
                data-vaul-no-drag=""
                className="focus-visible:shadow-focus-ring-button absolute right-8 top-7 z-10 flex h-8 w-8 items-center justify-center rounded-full bg-[#F7F8F9] text-[#949595] transition-transform focus:scale-95 active:scale-75"
              >
                <CloseIcon />
              </button>
            </Drawer.Close>

            <div ref={elementRef} className="px-6 pb-6 pt-2.5 antialiased">
              <AnimatePresence initial={false} mode="popLayout" custom={contentKey}>
                <motion.div
                  initial={{ opacity: 0, scale: 0.96 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.96 }}
                  key={contentKey}
                  transition={{
                    duration: opacityDuration,
                    ease: [0.26, 0.08, 0.25, 1],
                  }}
                >
                  {content}
                </motion.div>
              </AnimatePresence>
            </div>

          </motion.div>
        </Drawer.Content>
      </Drawer.Portal>
    </Drawer.Root>
  );
}
